import { gql, useQuery } from '@apollo/client';
import { Loading } from '@everlutionsk/ui';
import React, { createContext, useContext, useEffect } from 'react';
import { environment } from './environment';
import { AdminRole } from './graphql/types';

export type Identity = ReturnType<typeof useIdentity>;

const orgRole = {
  id: 'role-1',
  from: null,
  to: null,
  isArchived: false,
  isActive: true,
  permissionSets: [],
  roleAssetId: null,
  organisation: { label: 'aSign Admin', value: 'root' },
  role: { label: 'Owner', value: 'owner' }
};

const defaultIdentity = {
  id: 'admin',
  firstName: 'Admin',
  lastName: 'User',
  fullName: 'Admin User',
  email: 'admin@asign.sk',
  phoneNumber: null,
  systemRole: { label: 'Owner', value: 'owner' },
  avatar: null as { id: string; hash: string } | null,
  isPasswordSet: true,
  isActionConfirmationRecipient: false,
  role: 'admin' as AdminRole,
  permissions: {} as Record<string, number | '*'>,
  organisationRolesOptions: [orgRole],
  organisationRoles: [orgRole],
  organisationRoleActive: orgRole,
  organisation: {
    id: 'root',
    name: 'aSign Admin',
    permissions: [] as Array<{ section: string; value: string }>,
    colorTheme: null
  },
  language: { label: 'English', value: 'en_US' }
};

const ADMIN_IDENTITY_QUERY = gql<AdminIdentityGQL>`
  query AdminIdentity {
    adminIdentity {
      id
      email
      firstName
      lastName
      role
      isPasswordSet
      isActionConfirmationRecipient
    }
  }
`;

const context = createContext<typeof defaultIdentity>(defaultIdentity);

export function IdentityProvider({ children }: { children: React.ReactNode }) {
  const { data, loading } = useQuery(ADMIN_IDENTITY_QUERY, { fetchPolicy: 'cache-first' });

  useEffect(() => {
    if (loading) return;
    if (data != null) return;
    if (environment.isLocal) return;
    window.location.href = '/auth';
  }, [loading, data]);

  if (loading) return <Loading />;
  if (data == null) return <Loading />;

  const firstName = data.adminIdentity.firstName ?? '';
  const lastName = data.adminIdentity.lastName ?? '';
  // Build a real display name for the avatar initials; fall back to the email
  // (getInitials handles a single token) when no name has been set yet.
  const fullName = `${firstName} ${lastName}`.trim() || data.adminIdentity.email;

  const identity = {
    ...defaultIdentity,
    id: data.adminIdentity.id,
    email: data.adminIdentity.email,
    firstName,
    lastName,
    fullName,
    role: data.adminIdentity.role,
    isPasswordSet: data.adminIdentity.isPasswordSet,
    isActionConfirmationRecipient: data.adminIdentity.isActionConfirmationRecipient
  };

  return <context.Provider value={identity}>{children}</context.Provider>;
}

export function useIdentity() {
  const identity = useContext(context);
  return { ...identity, permissions: identity.permissions };
}

export function useIsSuperAdmin(): boolean {
  return useIdentity().role === 'superAdmin';
}
